import React, { useState, useEffect } from 'react';
import axios from 'axios';
import base64 from 'base64-js';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const AdminInterface = () => {
  const [activeTab, setActiveTab] = useState('viewUsers');
  const [userSubTab, setUserSubTab] = useState('teachersAndStudents');
  const [users, setUsers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [regularUsers, setRegularUsers] = useState([]);
  const [students, setStudents] = useState([]); // Holds students of a selected teacher
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [manualSubmissions, setManualSubmissions] = useState({ pending: [], finished: [] });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [error, setError] = useState('');
  const [finalSubmissions, setFinalSubmissions] = useState([]);
  const [tallyVotes, setTallyVotes] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [expandedTeachers, setExpandedTeachers] = useState({});
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [feedbackCompletedCount, setFeedbackCompletedCount] = useState(0);
  const [submissionMap, setSubmissionMap] = useState({});
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  

  const [stats, setStats] = useState({
    users: { total: 0, regular: 0, teachers: 0, students: 0 },
    manual_submissions: { total: 0, pending: 0, completed: 0 },
    newman_submissions: { total: 0 },
  });
  

  const getToken = () => {
    return localStorage.getItem('adminToken');
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setStats(response.data);
    } catch (error) {
      setError("Failed to fetch stats: " + error);
    }
  };
  
  useEffect(() => {
    fetchStats();
  }, []);
  

  const fetchFinalSubmissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/final-submissions/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = response.data;
  
      const grouped = {};
      data.forEach(sub => {
        const date = new Date(sub.submitted_at);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
  
        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = {};
        if (!grouped[year][month][day]) grouped[year][month][day] = [];
  
        grouped[year][month][day].push(sub);
      });
  
      setSubmissionMap(grouped);
      setFinalSubmissions(data);  // This remains the master list
      setFilteredSubmissions(data);  // Initially, show all submissions
    } catch (error) {
      setError("Failed to fetch final submissions: " + error);
    }
  };

  const fetchTallyVotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/tally-votes/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTallyVotes(response.data);
    } catch (error) {
      setError("Failed to fetch tally votes: " + error);
    }
  };

  const filterSubmissions = (year, month, day) => {
    let filtered = finalSubmissions;
  
    if (year) {
      filtered = filtered.filter(sub => {
        const date = new Date(sub.submitted_at);
        return date.getFullYear().toString() === year;
      });
    }
    if (month) {
      filtered = filtered.filter(sub => {
        const date = new Date(sub.submitted_at);
        return (date.getMonth() + 1).toString().padStart(2, '0') === month;
      });
    }
    if (day) {
      filtered = filtered.filter(sub => {
        const date = new Date(sub.submitted_at);
        return date.getDate().toString().padStart(2, '0') === day;
      });
    }
  
    setFilteredSubmissions(filtered);
  };
    
  useEffect(() => {
    if (activeTab === "finalSubmissions") {
      fetchFinalSubmissions();
    }
    if (activeTab === "tallyVotes") {
      fetchTallyVotes();
    }
  }, [activeTab]);
    
  const fetchUserSubmissions = async (userId, userName) => {
    try {
      const response = await axios.get(`${API_URL}/admin/user/${userId}/submissions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUserSubmissions(response.data);
      setSelectedUser(userName);
      setActiveTab('userSubmissions'); // Navigate to the user submissions tab
    } catch (error) {
      setError('Failed to fetch submissions for the user: ' + error);
    }
  };

  const fetchTallyUserDetails = async (userId) => {
    if (!userId) return;

    try {
      const response = await axios.get(`${API_URL}/admin/users/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const user = response.data.find((u) => u._id === userId);

      if (user) {
        setSelectedUserDetails(user);
      } else {
        alert("User details not found.");
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Failed to fetch user details.");
    }
  };
  
  
  const fetchStudentsForTeacher = async (groupName, teacherName) => {
    try {
      const response = await axios.get(`${API_URL}/admin/teacher/${groupName}/students`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setStudents(response.data);
      setSelectedTeacher(teacherName);
    } catch (error) {
      setError('Failed to fetch students for this group: ' + error);
      setStudents([]);
    }
  };
  
  
  const revertJinshi = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/admin/revert-jinshi/`,
        userId,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
        }
      );
      alert("Jinshi reverted successfully!");
    } catch (error) {
      console.error("Error reverting Jinshi:", error.response?.data || error.message);
      alert("Failed to revert Jinshi.");
    }
  };

  const handleDeleteManualSubmission = async (submissionId) => {
      const confirmDelete = window.confirm("Are you sure you want to delete this submission?");
      if (!confirmDelete) return;

      try {
        const token = localStorage.getItem("adminToken");

        const response = await fetch(
          `${API_URL}/admin/manual-submissions/${submissionId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || data.message || "Failed to delete submission");
        }

        setManualSubmissions((prev) => ({
          pending: prev.pending.filter((submission) => submission._id !== submissionId),
          finished: prev.finished.filter((submission) => submission._id !== submissionId),
        }));

        alert("Submission deleted successfully.");
      } catch (error) {
        console.error("Error deleting manual submission:", error);
        alert(error.message || "Failed to delete submission.");
      }
    };

  const handleDeleteFinalSubmission = async (submissionId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this final submission?");
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("adminToken");

      const response = await fetch(
        `${API_URL}/admin/final-submissions/${submissionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || "Failed to delete final submission");
      }

      setFinalSubmissions((prev) => prev.filter((submission) => submission._id !== submissionId));
      setFilteredSubmissions((prev) => prev.filter((submission) => submission._id !== submissionId));

      alert("Final submission deleted successfully.");
    } catch (error) {
      console.error("Error deleting final submission:", error);
      alert(error.message || "Failed to delete final submission.");
    }
  };

  
  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/users/`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
  
        const teachersData = response.data.filter((user) => user.type === "teacher");
        const studentsData = response.data.filter((user) => user.type === "student");
        const regularUsersData = response.data.filter((user) => user.type === "regular");
  
        // Group students under their respective teachers
        const teachersWithStudents = teachersData.map((teacher) => ({
          ...teacher,
          students: studentsData.filter(
            (student) => student.group_name === teacher.group_name
          ),
        }));
  
        setTeachers(teachersWithStudents);
        setRegularUsers(regularUsersData);
      } catch (error) {
        setError("Failed to fetch users: " + error);
      }
    };
    
    const fetchManualSubmissions = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/manual-submissions/`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
    
        const pendingSubmissions = response.data.filter((s) => s.status === "pending");
        const finishedSubmissions = response.data.filter((s) => s.status === "finished");
    
        setManualSubmissions({ pending: pendingSubmissions, finished: finishedSubmissions });
      } catch (error) {
        setError('Failed to fetch manual submissions: ' + error);
      }
    };
    
    fetchUsersData();
    fetchManualSubmissions();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedTeacher(null);
    setStudents([]);
    setSelectedSubmission(null);
    setFeedbackText('');
  };

  const handleProvideFeedback = async (submissionId, isFinal = false) => {
  try {
    const url = isFinal
      ? `${API_URL}/admin/final-submissions/feedback/`
      : `${API_URL}/admin/manual-submissions/feedback/`;

    await axios.post(
      url,
      {
        submission_id: submissionId,
        feedback: feedbackText,
      },
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    alert('Feedback submitted successfully');
    setSelectedSubmission(null);
    setFeedbackText('');

    if (isFinal) {
      fetchFinalSubmissions();
    } else {
      const response = await axios.get(`${API_URL}/admin/manual-submissions/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const pendingSubmissions = response.data.filter((s) => s.status === "pending");
      const finishedSubmissions = response.data.filter((s) => s.status === "finished");

      setManualSubmissions({
        pending: pendingSubmissions,
        finished: finishedSubmissions,
      });
    }
  } catch (error) {
    setError('Failed to submit feedback: ' + error);
  }
};
  const toggleTeacherExpansion = (teacherId) => {
    setExpandedTeachers((prev) => ({
      ...prev,
      [teacherId]: !prev[teacherId],
    }));
  };
  const awardJinshi = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/admin/award-jinshi/`,
        userId,  // Just pass the string directly
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
        }
      );
      alert("Jinshi awarded successfully!");
    } catch (error) {
      console.error("Error awarding Jinshi:", error.response?.data || error.message);
      alert("Failed to award Jinshi.");
    }
  };
  
  

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Admin Dashboard</h1>
      <header style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statCard}>
            <h3 style={styles.statTitle}>Total Users</h3>
            <p style={styles.statNumber}>{stats.users.total}</p>
            <p>
              Regular: {stats.users.regular} Teachers: {stats.users.teachers} Students:{" "}
              {stats.users.students}
            </p>
          </div>
          <div style={styles.statCard}>
            <h3 style={styles.statTitle}>Manual Submissions</h3>
            <p style={styles.statNumber}>
              {stats.manual_submissions.pending} / {stats.manual_submissions.total} Pending
            </p>
            <p>Completed: {stats.manual_submissions.completed}</p>
          </div>
          <div style={styles.statCard}>
            <h3 style={styles.statTitle}>Newman Prize Submissions</h3>
            <p style={styles.statNumber}>{stats.newman_submissions.total}</p>
          </div>
        </div>
      </header>

      {error && <p style={styles.error}>{error}</p>}

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tabButton, ...(activeTab === 'viewUsers' && styles.activeTab) }}
          onClick={() => handleTabChange('viewUsers')}
        >
          View Users
        </button>
        <button
          style={{ ...styles.tabButton, ...(activeTab === 'manualSubmissions' && styles.activeTab) }}
          onClick={() => handleTabChange('manualSubmissions')}
        >
          Manual Submissions
        </button>
        <button
        style={{ ...styles.tabButton, ...(activeTab === "finalSubmissions" && styles.activeTab) }}
        onClick={() => handleTabChange("finalSubmissions")}
        >
        Final Submissions
        </button>
        <button
        style={{ ...styles.tabButton, ...(activeTab === "tallyVotes" && styles.activeTab) }}
        onClick={() => handleTabChange("tallyVotes")}
        >
         Tally Votes
        </button>

      </div>

      {activeTab === 'viewUsers' && (
  <div style={styles.contentContainer}>
    <div style={styles.dropdownWrapper}>
      <label style={styles.dropdownLabel}>Select User Type:</label>
      <select
        style={styles.dropdown}
        value={userSubTab}
        onChange={(e) => setUserSubTab(e.target.value)}
      >
        <option value="teachersAndStudents">Teachers & Students</option>
        <option value="regularUsers">Regular Users</option>
      </select>
    </div>

    {userSubTab === 'teachersAndStudents' && (
      <div style={styles.sectionContainer}>
        <h3 style={styles.sectionTitle}>Teachers and Their Students</h3>
        <div style={styles.cardGrid}>
          {teachers.map((teacher) => (
            <div key={teacher._id} style={styles.teacherCard}>
              <h4 style={styles.teacherTitle}>{teacher.name}</h4>
              <p><strong>Email:</strong> {teacher.email || "N/A"}</p>
              <p><strong>School:</strong> {teacher.school_name || "N/A"}</p>
              <p><strong>Group:</strong> {teacher.group_name || "N/A"}</p>
              <p><strong>Region:</strong> {teacher.region || "N/A"}</p>

              <button
                style={styles.button}
                onClick={() => toggleTeacherExpansion(teacher._id)}
              >
                {expandedTeachers[teacher._id] ? 'Collapse' : 'Expand'}
              </button>

              {expandedTeachers[teacher._id] && (
                <div style={styles.studentsContainer}>
                  <h5 style={styles.sectionSubtitle}>Students:</h5>
                  {teacher.students.length > 0 ? (
                    teacher.students.map((student) => (
                      <div key={student._id} style={styles.studentCard}>
                        <h5 style={styles.studentTitle}>{student.username}</h5>
                        <p><strong>Rank:</strong> {student.rank || "Unranked"}</p>
                        <p><strong>Region:</strong> {teacher.region || "N/A"}</p>
                        <p><strong>Group:</strong> {student.group_name || "N/A"}</p>
                        <button
                          style={styles.button}
                          onClick={() => fetchUserSubmissions(student._id, student.username)}
                        >
                          View Submissions
                        </button>
                      </div>
                    ))
                  ) : (
                    <p style={styles.noDataText}>No students yet for this teacher's group.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {userSubTab === 'regularUsers' && (
      <div style={styles.sectionContainer}>
        <h3 style={styles.sectionTitle}>Regular Users</h3>
        <div style={styles.cardGrid}>
          {regularUsers.map((user) => (
            <div key={user.email} style={styles.userCard}>
              <h4 style={styles.userTitle}>{user.name}</h4>
              <p><strong>Email:</strong> {user.email || "N/A"}</p>
              <p><strong>Rank:</strong> {user.rank || "None"}</p>
              <button
                style={styles.button}
                onClick={() => fetchUserSubmissions(user._id, user.name)}
              >
                View Submissions
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
)}


{activeTab === 'userSubmissions' && (
  <div style={styles.contentContainer}>
    <h3 style={styles.sectionTitle}>
      Submissions by {selectedUser}
    </h3>
    {userSubmissions.length === 0 ? (
      <p>No submissions found for this user.</p>
    ) : (
      Object.entries(
        userSubmissions.reduce((grouped, submission) => {
          const level = submission.submission_level || "Unknown";
          if (!grouped[level]) grouped[level] = [];
          grouped[level].push(submission);
          return grouped;
        }, {})
      ).map(([level, submissions]) => (
        <div key={level} style={styles.levelContainer}>
          <h4 style={styles.levelTitle}>{level} Submissions</h4>
          <div style={styles.cardGrid}>
            {submissions.map((submission) => (
              <div key={submission._id} style={styles.submissionCard}>
                <h4 style={styles.submissionTitle}>
                  {submission.submission_level} Submission
                  {submission.submission_level === "jinshi/final" && submission.poem_type && (
                    <span style={styles.poemType}> ({submission.poem_type})</span>
                  )}
                </h4>
                <p><strong>Status:</strong> {submission.status || "Unknown"}</p>
                <p><strong>Submitted At:</strong> {new Date(submission.submitted_at).toLocaleString()}</p>
                <p><strong>Type:</strong> {submission.submission_type}</p>
                <p><strong>Submitted Content:</strong></p>
                <div style={styles.submissionContent}>
                  {submission.couplet ? (
                    // Couplet Display
                    <div>
                      {submission.couplet.split("\n").map((line, index) => (
                        <p key={index} style={styles.poemLine}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    // Poem Display
                    submission.poem && (
                      <div>
                        {submission.poem.split("\n").map((line, index) => (
                          <p key={index} style={styles.poemLine}>{line}</p>
                        ))}
                      </div>
                    )
                  )}
                </div>

                {/* View Feedback Button */}
                <button
                  style={styles.button}
                  onClick={() =>
                    setSelectedFeedback(submission.model_assessment || "No feedback provided yet.")
                  }
                >
                  View Feedback
                </button>
              </div>
            ))}
          </div>
        </div>
      ))
    )}

    <button
      style={styles.button}
      onClick={() => handleTabChange('viewUsers')}
    >
      Back to Users
    </button>
  </div>
)}

{/* Feedback Modal */}
{selectedFeedback && (
  <div style={styles.feedbackModal}>
    <div style={styles.feedbackContent}>
      <h3>Feedback</h3>
      <p style={styles.feedbackText}>{selectedFeedback}</p>
      <button
        style={styles.button}
        onClick={() => setSelectedFeedback(null)}
      >
        Close
      </button>
    </div>
  </div>
)}


{activeTab === 'manualSubmissions' && (
  <div style={styles.contentContainer}>
    {/* Pending Submissions */}
    <h3 style={styles.sectionTitle}>Pending Submissions</h3>
    {manualSubmissions.pending.length === 0 ? (
      <p>No pending submissions.</p>
    ) : (
      <div style={styles.cardGrid}>
        {manualSubmissions.pending.map((submission) => (
          <div key={submission._id} style={styles.submissionCard}>
            <p><strong>User Name:</strong> {submission.user_name || 'Unknown'}</p>
            <p><strong>User Level:</strong> {submission.user_level || 'Unranked'}</p>
            <p><strong>User Type:</strong> {submission.user_type || 'Unknown'}</p>
            <p><strong>submission_level:</strong> {submission.submission_level || 'unknown'}</p>
            <p>
              <strong>Submitted at:</strong>{" "}
              {submission.submitted_at
                ? new Date(submission.submitted_at).toLocaleString()
                : "N/A"}
            </p>
            <p><strong>Submitted Content:</strong></p>
            <div style={styles.submissionContent}>
              {submission.couplet ? (
                <div>
                  {submission.couplet.split("\n").map((line, index) => (
                    <p key={index} style={styles.poemLine}>{line}</p>
                  ))}
                </div>
              ) : (
                submission.poem && (
                  <div>
                    {submission.poem.split("\n").map((line, index) => (
                      <p key={index} style={styles.poemLine}>{line}</p>
                    ))}
                  </div>
                )
              )}
            </div>
            <button
              style={styles.button}
              onClick={() => setSelectedSubmission(submission)}
            >
              Provide Feedback
            </button>

            <button
              style={{
                ...styles.button,
                backgroundColor: "#b04a4a",
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}
              onClick={() => handleDeleteManualSubmission(submission._id)}
            >
              🗑 Delete
            </button>
          </div>
        ))}
      </div>
    )}


    {/* Finished Submissions */}
    <h3 style={styles.sectionTitle}>Finished Submissions</h3>
    {manualSubmissions.finished.length === 0 ? (
      <p>No finished submissions.</p>
    ) : (
      <div style={styles.cardGrid}>
        {manualSubmissions.finished.map((submission) => (
          <div key={submission._id} style={styles.submissionCard}>
            <p><strong>User Name:</strong> {submission.user_name || 'Unknown'}</p>
            <p><strong>User Level:</strong> {submission.user_level || 'Unranked'}</p>
            <p><strong>User Type:</strong> {submission.user_type || 'Unknown'}</p>
            <p><strong>submission_level:</strong> {submission.submission_level || 'unknown'}</p>
            <p>
              <strong>Submitted at:</strong>{" "}
              {submission.submitted_at
                ? new Date(submission.submitted_at).toLocaleString()
                : "N/A"}
            </p>
            <p><strong>Submitted Content:</strong></p>
            <div style={styles.submissionContent}>
              {submission.couplet ? (
                <div>
                  {submission.couplet.split("\n").map((line, index) => (
                    <p key={index} style={styles.poemLine}>{line}</p>
                  ))}
                </div>
              ) : (
                submission.poem && (
                  <div>
                    {submission.poem.split("\n").map((line, index) => (
                      <p key={index} style={styles.poemLine}>{line}</p>
                    ))}
                  </div>
                )
              )}
            </div>
            <p><strong>Feedback:</strong> {submission.feedback || 'No feedback provided'}</p>
            <button
            style={{
              ...styles.button,
              backgroundColor: "#b04a4a",
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
            onClick={() => handleDeleteManualSubmission(submission._id)}
          >
            🗑 Delete
          </button>

        </div>
          ))}
      </div>
    )}
  </div>
)}


{selectedSubmission && (
  <div style={styles.feedbackModal}>
    <div style={styles.feedbackContent}>
      <h3>Provide Feedback</h3>
      <p>
        <strong>{selectedSubmission.couplet ? 'Couplet' : 'Poem'}:</strong> {selectedSubmission.couplet || selectedSubmission.poem}
      </p>
      <textarea
        style={styles.feedbackTextArea}
        placeholder="Enter your feedback here..."
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
      />
      <button
        style={{...styles.button, marginRight: "12px"}}
        onClick={() => handleProvideFeedback(selectedSubmission._id,selectedSubmission.submission_level === "jinshi/final")}
      >
        Submit Feedback
      </button>
      <button
        style={styles.button}
        onClick={() => setSelectedSubmission(null)}
      >
        Cancel
      </button>
    </div>
  </div>
)}

{activeTab === "finalSubmissions" && (
  <div style={styles.contentContainer}>
    <h3 style={styles.sectionTitle}>Final Submissions</h3>

    <div style={{ marginBottom: '20px' }}>
      <label>Year: </label>
      <select
        value={selectedYear}
        onChange={(e) => {
          setSelectedYear(e.target.value);
          filterSubmissions(e.target.value, selectedMonth, selectedDay);
        }}
      >
        <option value="">All</option>
        {Object.keys(submissionMap).sort().reverse().map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>

      <label style={{ marginLeft: '15px' }}>Month: </label>
      <select
        value={selectedMonth}
        onChange={(e) => {
          setSelectedMonth(e.target.value);
          filterSubmissions(selectedYear, e.target.value, selectedDay);
        }}
      >
        <option value="">All</option>
        {selectedYear && Object.keys(submissionMap[selectedYear] || {}).sort().map((month) => (
          <option key={month} value={month}>{month}</option>
        ))}
      </select>

      <label style={{ marginLeft: '15px' }}>Date: </label>
      <select
        value={selectedDay}
        onChange={(e) => {
          setSelectedDay(e.target.value);
          filterSubmissions(selectedYear, selectedMonth, e.target.value);
        }}
      >
        <option value="">All</option>
        {selectedMonth && selectedYear && Object.keys(submissionMap[selectedYear]?.[selectedMonth] || {}).sort().map((day) => (
          <option key={day} value={day}>{day}</option>
        ))}
      </select>
      {selectedMonth && selectedYear && (
  <p style={{ fontSize: '0.9rem', color: '#777', marginTop: '5px', marginLeft: '5px' }}>
    (Only dates with submissions are shown)
  </p>
)}

    </div>

    {filteredSubmissions.length > 0 ? (
      <div style={styles.cardGrid}>
        {filteredSubmissions.map((submission) => (
          <div key={submission._id} style={styles.submissionCard}>
            <h4 style={styles.submissionTitle}>{submission.title || 'Final Submission'}</h4>
            
            <p><strong>Poem Type:</strong> {submission.poem_type || 'Unknown'}</p>
            <p><strong>User Type:</strong> {submission.user_type || 'Unknown'}</p>
            <p><strong>User Level:</strong> {submission.user_level || 'Unranked'}</p>

            {submission.user_type === "student" ? (
              <>
                <p><strong>Username:</strong> {submission.username || "Unknown"}</p>
                <p><strong>Group Name:</strong> {submission.group_name || "N/A"}</p>
                <p><strong>Teacher:</strong> {submission.teacher_name || "Unknown"}</p>
                <p><strong>School:</strong> {submission.school_name || "N/A"}</p>
                <p><strong>Region:</strong> {submission.region || "N/A"}</p>
              </>
            ) : (
              <p><strong>User Name:</strong> {submission.user_name || 'Unknown'}</p>
            )}

            <p>
              <strong>Submitted At:</strong>{" "}
              {submission.submitted_at
                ? new Date(submission.submitted_at).toLocaleString()
                : "N/A"}
            </p>

            <p><strong>Submitted Content:</strong></p>
            <div style={styles.submissionContent}>
              {submission.submitted_content.split("\n").map((line, index) => (
                <p key={index} style={styles.poemLine}>{line}</p>
              ))}
            </div>

            {submission.feedback && (
              <p><strong>Feedback:</strong> {submission.feedback}</p>
            )}

            {/* Award Jinshi Button */}
            {submission.user_type !== "guest" && (
              <>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
                  {!submission.has_jinshi && (
                    <button
                      style={styles.button}
                      onClick={() => {
                        awardJinshi(submission.user_id);
                        fetchFinalSubmissions();
                      }}
                    >
                      Award Jinshi
                    </button>
                  )}

                  {submission.has_jinshi && (
                    <button
                      style={{ ...styles.button, backgroundColor: '#d9534f' }}
                      onClick={() => {
                        revertJinshi(submission.user_id);
                        fetchFinalSubmissions();
                      }}
                    >
                      Revert Jinshi
                    </button>
                  )}

                  <button
                    style={styles.button}
                    onClick={() => setSelectedSubmission(submission)}
                  >
                    Provide Feedback
                  </button>
                </div>

                <button
                  style={{
                    ...styles.button,
                    marginTop: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                  onClick={() => handleDeleteFinalSubmission(submission._id)}
                >
                  🗑 Delete
                </button>
              </>
            )}

          </div>
        ))}
      </div>
    ) : (
      <p>No submissions found for selected filters.</p>
    )}
  </div>
)}

{activeTab === "tallyVotes" && (
  <div style={styles.contentContainer}>
    <h3 style={styles.sectionTitle}>Tally Votes</h3>

    {tallyVotes.length > 0 ? (
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Username</th>
              <th style={styles.tableHeader}>User Type</th>
              <th style={styles.tableHeader}>Poem Title</th>
              <th style={styles.tableHeader}>Total Ratings</th>
              <th style={styles.tableHeader}>Average Score</th>
              <th style={styles.tableHeader}>Breakdown</th>
            </tr>
          </thead>
          <tbody>
            {tallyVotes.map((item) => (
              <tr key={item._id}>
                <td style={styles.tableCell}>
                  <span
                    style={styles.clickableUserName}
                    onClick={() => fetchTallyUserDetails(item.user_id)}
                  >
                    {item.user_name}
                  </span>
                </td>
                <td style={styles.tableCell}>{item.user_type}</td>
                <td style={styles.tableCell}>{item.title}</td>
                <td style={styles.tableCell}>{item.total_ratings}</td>
                <td style={styles.tableCell}>{item.average_score}</td>
                <td style={styles.tableCell}>
                  1★={item.score_breakdown["1"]}, 2★={item.score_breakdown["2"]}, 3★={item.score_breakdown["3"]}, 4★={item.score_breakdown["4"]}, 5★={item.score_breakdown["5"]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p>No rated final submissions found.</p>
    )}
  </div>
)}


{selectedFeedback && (
  <div style={styles.feedbackModal}>
    <div style={styles.feedbackContent}>
      <h3>Feedback</h3>
      <p style={styles.feedbackText}>{selectedFeedback}</p>
      <button
        style={styles.button}
        onClick={() => setSelectedFeedback(null)}
      >
        Close
      </button>
    </div>
  </div>
)}

{selectedUserDetails && (
  <div style={styles.feedbackModal}>
    <div style={styles.feedbackContent}>
      <h3>User Details</h3>

      {selectedUserDetails.name && (
        <p><strong>Name:</strong> {selectedUserDetails.name}</p>
      )}

      {selectedUserDetails.username && (
        <p><strong>Username:</strong> {selectedUserDetails.username}</p>
      )}

      {selectedUserDetails.email && (
        <p><strong>Email:</strong> {selectedUserDetails.email}</p>
      )}

      {selectedUserDetails.type && (
        <p><strong>User Type:</strong> {selectedUserDetails.type}</p>
      )}

      {selectedUserDetails.school_name && (
        <p><strong>School:</strong> {selectedUserDetails.school_name}</p>
      )}

      {selectedUserDetails.group_name && (
        <p><strong>Group:</strong> {selectedUserDetails.group_name}</p>
      )}

      {selectedUserDetails.region && (
        <p><strong>Region:</strong> {selectedUserDetails.region}</p>
      )}

      {selectedUserDetails.rank && (
        <p><strong>Level:</strong> {selectedUserDetails.rank}</p>
      )}

      <button
        style={styles.button}
        onClick={() => setSelectedUserDetails(null)}
      >
        Close
      </button>
    </div>
  </div>
)}
    </div>
  );
};

const styles = {
  sectionContainer: {
    marginBottom: '30px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '15px',
  },
  
  studentsContainer: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #ddd',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  
  sectionTitle: {
    fontSize: '1.5em',
    marginBottom: '15px',
    color: '#3d405b',
    textAlign: 'left',
  },
  sectionSubtitle: {
    fontSize: '1.2em',
    fontWeight: '600',
    color: '#4a4e69',
    marginBottom: '10px',
  },
  
  userTitle: {
    marginBottom: '10px',
    fontWeight: '600',
    fontSize: '1.2em',
    color: '#4a4e69',
  },
  
  noDataText: {
    color: '#777',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: 'Arial, sans-serif',
    color: '#333',
    backgroundColor: '#f0f2f5',
    minHeight: '100vh',
    padding: '20px',
  },
  title: {
    fontSize: '2em',
    marginBottom: '10px',
    color: '#3d405b',
  },
  header: {
    width: '100%',
    backgroundColor: '#3d405b',
    color: '#ffffff',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '30px',
    textAlign: 'center',
  },
  stats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '10px',
  },
  statCard: {
    backgroundColor: '#6a7592',
    color: '#ffffff',
    padding: '15px 25px',
    borderRadius: '8px',
    width: '180px',
    textAlign: 'center',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.3s',
  },
  statTitle: {
    fontSize: '1.1em',
    fontWeight: '600',
    color: '#ffffff',
  },
  statNumber: {
    fontSize: '1.6em',
    fontWeight: '700',
    color: '#c9d1da',
  },
  tabs: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: '20px',
  },
  tabButton: {
    backgroundColor: '#4a4e69',
    color: 'white',
    border: 'none',
    padding: '12px 25px',
    margin: '0 10px',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background 0.3s',
  },
  activeTab: {
    backgroundColor: '#9a8c98',
  },
  contentContainer: {
    display: 'inline-block',
    width: '100%',
    maxWidth: '1000px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px',
  },
  userCard: {
    backgroundColor: '#f7f5f2',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s',
    textAlign: 'left',
  },
  teacherCard: {
    backgroundColor: '#eaecef',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
    position: 'relative',
  },
  
  teacherTitle: {
    fontSize: '1.4em',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '10px',
  },
  
  studentsContainer: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #ddd',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  
  studentCard: {
    backgroundColor: '#ffffff',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '10px',
    border: '1px solid #ddd',
  },
  
  studentTitle: {
    fontSize: '1.1em',
    fontWeight: '500',
    color: '#34495e',
    marginBottom: '8px',
  },
  
  noDataText: {
    color: '#777',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  
  submissionCard: {
    backgroundColor: '#f7f5f2',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
    textAlign: 'left',
    transition: 'transform 0.3s',
    display: 'flex',
    flexDirection: 'column',
  },
  submissionTitle: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#3d405b',
  },
  button: {
    backgroundColor: '#9a8c98',
    color: 'white',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    margin: '5px 0',
  },
  poemType: {
    fontSize: '0.9em',
    fontWeight: 'normal',
    color: '#6a7592',
  },
  submissionContent: {
    marginTop: '10px',
    padding: '15px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
    whiteSpace: 'pre', // Preserve whitespace and ensure line breaks are honored
    overflowX: 'auto', // Add horizontal scroll if the content exceeds the width
    wordWrap: 'normal', // Prevent wrapping of words
    fontFamily: 'monospace', // Consistent font style for poetry
    fontSize: '1.1em',
  },
  levelContainer: {
    marginBottom: '30px',
  },
  levelTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
  },
  
  
  poemLine: {
    margin: '0', // No margin between lines
    lineHeight: '1.5', // Adjust line height for better readability
    color: '#34495e',
  },
  feedbackModal: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  feedbackContent: {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    textAlign: 'center',
  },
  feedbackTextArea: {
    width: '100%',
    height: '100px',
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    marginTop: '10px',
    resize: 'vertical',
  },
  error: {
    color: 'red',
    marginBottom: '10px',
  },
  submissionCard: {
    backgroundColor: "#f7f5f2",
    padding: "15px",
    borderRadius: "8px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s",
    textAlign: "left",
    marginBottom: "20px",
  },
  poemContent: {
    whiteSpace: 'pre-wrap', // Preserve line breaks and wrap text
    fontFamily: 'monospace', // Optional: Set a specific font for poetry
    fontSize: '1rem',
    color: '#333',
    marginTop: '10px',
    display: 'block',
  },
  feedbackText: {
    fontSize: '1rem',
    color: '#333',
    textAlign: 'left',
    margin: '10px 0',
  },
  dropdownWrapper: {
    marginBottom: '20px',
    textAlign: 'left',
  },
  
  dropdownLabel: {
    marginRight: '10px',
    fontWeight: 'bold',
    fontSize: '1rem',
    color: '#333',
  },
  
  dropdown: {
    padding: '8px 12px',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    color: '#333',
  },

  tableWrapper: {
  width: '100%',
  overflowX: 'auto',
},

table: {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '20px',
  backgroundColor: '#ffffff',
},

tableHeader: {
  backgroundColor: '#4a4e69',
  color: '#ffffff',
  padding: '12px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  fontWeight: '600',
},

tableCell: {
  padding: '12px',
  borderBottom: '1px solid #ddd',
  color: '#333',
  verticalAlign: 'top',
},

clickableUserName: {
  cursor: 'pointer',
  textDecoration: 'underline',
  color: '#3d405b',
  fontWeight: '500',
},
  
  
};

export default AdminInterface;
